"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import React, { useCallback, useMemo, useSyncExternalStore } from "react";
import { motion, AnimatePresence } from "framer-motion";
import BrainMobileTabs from "./mobile/BrainMobileTabs";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { commonApiFetch } from "@/services/api/common-api";
import BrainDesktopDrop from "./BrainDesktopDrop";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { DropSize } from "@/helpers/waves/drop.helpers";
import { useWaveData } from "@/hooks/useWaveData";
import { useWaveTimers } from "@/hooks/useWaveTimers";
import { QueryKey } from "../react-query-wrapper/ReactQueryWrapper";
import { useWave } from "@/hooks/useWave";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import MemesQuickVoteDialog from "./left-sidebar/waves/memes-quick-vote/MemesQuickVoteDialog";
import { getActiveWaveIdFromUrl, getHomeRoute, getWaveHomeRoute } from "@/helpers/navigation.helpers";
import { markDropCloseNavigation } from "@/helpers/drop-close-navigation.helpers";
import CreateWaveModal from "@/components/waves/create-wave/CreateWaveModal";
import CreateDirectMessageModal from "@/components/waves/create-dm/CreateDirectMessageModal";
import { useAuth } from "@/components/auth/Auth";
import { useMyStreamOptional } from "@/contexts/wave/MyStreamContext";
import { useClosingDropId } from "@/hooks/useClosingDropId";
import { useMemesQuickVoteDialogController } from "@/hooks/useMemesQuickVoteDialogController";
import BrainMobileViewContent from "./mobile/BrainMobileViewContent";
import { BrainView } from "./mobile/brainMobileViews";
import { useBrainMobileActiveView } from "./mobile/useBrainMobileActiveView";
interface Props {
  readonly children: ReactNode;
}
const BrainMobile: React.FC<Props> = t0 => {
  const $ = _c(95);
  const {
    children
  } = t0;
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const {
    isApp
  } = useDeviceInfo();
  const {
    connectedProfile
  } = useAuth();
  const hasAuthenticatedProfile = Boolean(connectedProfile?.handle);
  const quickVote = useMemesQuickVoteDialogController();
  const hydrated = useSyncExternalStore(_temp2, _temp3, _temp4);
  const myStream = useMyStreamOptional();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.get("drop") ?? undefined;
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const dropId = t1;
  const {
    effectiveDropId,
    beginClosingDrop
  } = useClosingDropId(dropId);
  let t2;
  let t3;
  if ($[2] !== effectiveDropId) {
    t2 = [QueryKey.DROP, {
      drop_id: effectiveDropId
    }];
    t3 = async () => {
      if (!effectiveDropId) {
        throw new Error("Cannot fetch drop without a drop id");
      }
      return await commonApiFetch({
        endpoint: `drops/${effectiveDropId}`
      });
    };
    $[2] = effectiveDropId;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  const t4 = !!effectiveDropId;
  let t5;
  if ($[5] !== t2 || $[6] !== t3 || $[7] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      placeholderData: keepPreviousData,
      enabled: t4
    };
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const {
    data: drop
  } = useQuery(t5);
  let t6;
  if ($[9] !== myStream?.activeWave.id || $[10] !== pathname || $[11] !== searchParams) {
    t6 = myStream?.activeWave.id ?? getActiveWaveIdFromUrl({
      pathname,
      searchParams
    }) ?? null;
    $[9] = myStream?.activeWave.id;
    $[10] = pathname;
    $[11] = searchParams;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const waveId = t6;
  let t7;
  if ($[13] !== isApp || $[14] !== pathname || $[15] !== router || $[16] !== searchParams) {
    t7 = () => {
      const params = new URLSearchParams(searchParams.toString() || "");
      params.delete("wave");
      const basePath = getWaveHomeRoute({
        isDirectMessage: pathname.startsWith("/messages"),
        isApp
      });
      const newUrl = params.toString() ? `${basePath}?${params.toString()}` : basePath || getHomeRoute();
      router.push(newUrl, {
        scroll: false
      });
    };
    $[13] = isApp;
    $[14] = pathname;
    $[15] = router;
    $[16] = searchParams;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t7 || $[19] !== waveId) {
    t8 = {
      waveId,
      onWaveNotFound: t7
    };
    $[18] = t7;
    $[19] = waveId;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const {
    data: wave
  } = useWaveData(t8);
  const {
    isMemesWave,
    isCurationWave,
    isRankWave
  } = useWave(wave);
  const {
    voting: t9,
    decisions: t10
  } = useWaveTimers(wave);
  const {
    isCompleted
  } = t9;
  const {
    firstDecisionDone
  } = t10;
  let t11;
  if ($[21] !== firstDecisionDone || $[22] !== hasAuthenticatedProfile || $[23] !== isApp || $[24] !== isCompleted || $[25] !== isCurationWave || $[26] !== isMemesWave || $[27] !== isRankWave || $[28] !== pathname || $[29] !== searchParams || $[30] !== wave || $[31] !== waveId) {
    t11 = {
      firstDecisionDone,
      isApp,
      isCompleted,
      hasAuthenticatedProfile,
      isCurationWave,
      isMemesWave,
      isRankWave,
      pathname,
      searchParams,
      wave,
      waveId
    };
    $[21] = firstDecisionDone;
    $[22] = hasAuthenticatedProfile;
    $[23] = isApp;
    $[24] = isCompleted;
    $[25] = isCurationWave;
    $[26] = isMemesWave;
    $[27] = isRankWave;
    $[28] = pathname;
    $[29] = searchParams;
    $[30] = wave;
    $[31] = waveId;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  const {
    activeView,
    onViewChange
  } = useBrainMobileActiveView(t11);
  let t12;
  if ($[33] !== pathname || $[34] !== router || $[35] !== searchParams) {
    t12 = selectedDrop => {
      const params_0 = new URLSearchParams(searchParams.toString() || "");
      params_0.set("drop", selectedDrop.id);
      router.push(`${pathname}?${params_0.toString()}`, {
        scroll: false
      });
    };
    $[33] = pathname;
    $[34] = router;
    $[35] = searchParams;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  const onDropClick = t12;
  let t13;
  if ($[37] !== beginClosingDrop || $[38] !== dropId || $[39] !== pathname || $[40] !== router || $[41] !== searchParams) {
    t13 = () => {
      if (dropId) {
        beginClosingDrop(dropId);
      }
      markDropCloseNavigation();
      const params_1 = new URLSearchParams(searchParams.toString() || "");
      params_1.delete("drop");
      const newUrl_0 = params_1.toString() ? `${pathname}?${params_1.toString()}` : pathname || getHomeRoute();
      router.replace(newUrl_0, {
        scroll: false
      });
    };
    $[37] = beginClosingDrop;
    $[38] = dropId;
    $[39] = pathname;
    $[40] = router;
    $[41] = searchParams;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  const onDropClose = t13;
  let t14;
  if ($[43] !== drop || $[44] !== effectiveDropId) {
    t14 = !!effectiveDropId && !!drop && drop.id.toLowerCase() === effectiveDropId.toLowerCase();
    $[43] = drop;
    $[44] = effectiveDropId;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  const isDropOpen = t14;
  const hasWave = Boolean(waveId);
  let t15;
  if ($[46] !== pathname || $[47] !== router || $[48] !== searchParams) {
    t15 = () => {
      const params_2 = new URLSearchParams(searchParams.toString() || "");
      params_2.delete("create");
      const base = pathname;
      const next = params_2.toString() ? `${base}?${params_2.toString()}` : base;
      router.replace(next, {
        scroll: false
      });
    };
    $[46] = pathname;
    $[47] = router;
    $[48] = searchParams;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  const closeCreateOverlay = t15;
  let t16;
  bb0: {
    if (!isApp) {
      t16 = null;
      break bb0;
    }
    const createParam = searchParams.get("create");
    if (!createParam) {
      t16 = null;
      break bb0;
    }
    if (!connectedProfile) {
      t16 = null;
      break bb0;
    }
    if (createParam === "dm") {
      let t17;
      if ($[50] !== closeCreateOverlay || $[51] !== connectedProfile) {
        t17 = <CreateDirectMessageModal isOpen={true} onClose={closeCreateOverlay} profile={connectedProfile} />;
        $[50] = closeCreateOverlay;
        $[51] = connectedProfile;
        $[52] = t17;
      } else {
        t17 = $[52];
      }
      t16 = t17;
      break bb0;
    }
    if (createParam === "wave") {
      let t17;
      if ($[53] !== closeCreateOverlay || $[54] !== connectedProfile) {
        t17 = <CreateWaveModal isOpen={true} onClose={closeCreateOverlay} profile={connectedProfile} />;
        $[53] = closeCreateOverlay;
        $[54] = connectedProfile;
        $[55] = t17;
      } else {
        t17 = $[55];
      }
      t16 = t17;
      break bb0;
    }
    t16 = null;
  }
  const createOverlay = t16;
  const shouldMountQuickVoteDialog = quickVote.isQuickVoteOpen || activeView === BrainView.WAVES;
  const dropOverlayClass = isApp ? "tw-fixed tw-inset-0 tw-z-[1010] tw-bg-black tailwind-scope" : "tw-absolute tw-inset-0 tw-z-[1010]";
  let t17;
  if ($[56] !== drop || $[57] !== dropOverlayClass || $[58] !== isDropOpen || $[59] !== onDropClose) {
    t17 = isDropOpen && <div className={dropOverlayClass}><BrainDesktopDrop drop={{
        type: DropSize.FULL,
        ...drop,
        stableKey: drop.id,
        stableHash: drop.id
      }} onClose={onDropClose} /></div>;
    $[56] = drop;
    $[57] = dropOverlayClass;
    $[58] = isDropOpen;
    $[59] = onDropClose;
    $[60] = t17;
  } else {
    t17 = $[60];
  }
  let t18;
  if ($[61] !== activeView || $[62] !== hasWave || $[63] !== hydrated || $[64] !== isApp || $[65] !== onViewChange || $[66] !== wave) {
    t18 = (hasWave || !isApp) && <BrainMobileTabs activeView={activeView} onViewChange={onViewChange} wave={wave} waveActive={hasWave} showWavesTab={hydrated} showStreamBack={hydrated} isApp={isApp} />;
    $[61] = activeView;
    $[62] = hasWave;
    $[63] = hydrated;
    $[64] = isApp;
    $[65] = onViewChange;
    $[66] = wave;
    $[67] = t18;
  } else {
    t18 = $[67];
  }
  let t19;
  let t20;
  let t21;
  let t22;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      opacity: 0,
      x: 20
    };
    t20 = {
      opacity: 1,
      x: 0
    };
    t21 = {
      opacity: 0,
      x: -20
    };
    t22 = {
      duration: 0.2,
      ease: "easeInOut"
    };
    $[68] = t19;
    $[69] = t20;
    $[70] = t21;
    $[71] = t22;
  } else {
    t19 = $[68];
    t20 = $[69];
    t21 = $[70];
    t22 = $[71];
  }
  let t23;
  if ($[72] !== activeView || $[73] !== children || $[74] !== isCurationWave || $[75] !== isMemesWave || $[76] !== isRankWave || $[77] !== onDropClick || $[78] !== quickVote.openQuickVote || $[79] !== quickVote.prefetchQuickVote || $[80] !== wave || $[81] !== waveId) {
    t23 = <BrainMobileViewContent activeView={activeView} activeWaveId={waveId} isCurationWave={isCurationWave} isMemesWave={isMemesWave} isRankWave={isRankWave} onDropClick={onDropClick} onOpenQuickVote={quickVote.openQuickVote} onPrefetchQuickVote={quickVote.prefetchQuickVote} wave={wave}>{children}</BrainMobileViewContent>;
    $[72] = activeView;
    $[73] = children;
    $[74] = isCurationWave;
    $[75] = isMemesWave;
    $[76] = isRankWave;
    $[77] = onDropClick;
    $[78] = quickVote.openQuickVote;
    $[79] = quickVote.prefetchQuickVote;
    $[80] = wave;
    $[81] = waveId;
    $[82] = t23;
  } else {
    t23 = $[82];
  }
  let t24;
  if ($[83] !== activeView || $[84] !== t23) {
    t24 = <AnimatePresence mode="wait"><motion.div key={activeView} initial={t19} animate={t20} exit={t21} transition={t22} className="tw-relative tw-min-w-0 tw-flex-1">{t23}</motion.div></AnimatePresence>;
    $[83] = activeView;
    $[84] = t23;
    $[85] = t24;
  } else {
    t24 = $[85];
  }
  let t25;
  if ($[86] !== quickVote.dialogState || $[87] !== shouldMountQuickVoteDialog) {
    t25 = shouldMountQuickVoteDialog && <MemesQuickVoteDialog {...quickVote.dialogState} />;
    $[86] = quickVote.dialogState;
    $[87] = shouldMountQuickVoteDialog;
    $[88] = t25;
  } else {
    t25 = $[88];
  }
  let t26;
  if ($[89] !== createOverlay || $[90] !== t17 || $[91] !== t18 || $[92] !== t24 || $[93] !== t25) {
    t26 = <div className="tw-relative tw-flex tw-h-full tw-flex-col">{createOverlay}{t17}{t18}{t24}{t25}</div>;
    $[89] = createOverlay;
    $[90] = t17;
    $[91] = t18;
    $[92] = t24;
    $[93] = t25;
    $[94] = t26;
  } else {
    t26 = $[94];
  }
  return t26;
};
export default BrainMobile;
function _temp() {}
function _temp2() {
  return _temp;
}
function _temp3() {
  return true;
}
function _temp4() {
  return false;
}